import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { walkyAccountsAPI, type BroadcastResult } from "../api";
import { useAuthStore } from "../store/authStore";
import { Loader2, Send } from "lucide-react";

/** Matches the backend's chat message cap. */
const MAX_LENGTH = 5000;
const ALL_SCHOOLS = "all";

const sum = (results: BroadcastResult[], key: "recipients" | "pushed") =>
  results.reduce((total, r) => total + r[key], 0);

/**
 * Broadcast a DM from Walky Official.
 *
 * Each school's official account DMs everyone at that school. It is an
 * ordinary direct message, so it lands in the Messages inbox with a push like
 * any other, and replies go to that school's Walky account.
 */
export function WalkyBroadcastPage() {
  const { isAuthenticated } = useAuthStore();

  const [schoolId, setSchoolId] = useState(ALL_SCHOOLS);
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState<BroadcastResult[] | null>(null);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data: accounts, isLoading } = useQuery({
    queryKey: ["walky-accounts"],
    queryFn: walkyAccountsAPI.list,
    enabled: isAuthenticated,
  });

  const schools = (accounts ?? []).flatMap((a) => (a.school ? [a.school] : []));
  const schoolName = (id: string) =>
    schools.find((s) => s._id === id)?.school_name ?? id;
  const targetIds =
    schoolId === ALL_SCHOOLS ? schools.map((s) => s._id) : [schoolId];

  const previewMutation = useMutation({
    mutationFn: () => walkyAccountsAPI.broadcast(targetIds, content, true),
    onSuccess: setPreview,
    onError: () =>
      setFeedback({ type: "error", message: "Could not count the recipients." }),
  });

  const sendMutation = useMutation({
    mutationFn: () => walkyAccountsAPI.broadcast(targetIds, content),
    onSuccess: (results) => {
      setPreview(null);
      setContent("");
      const failed = results.filter((r) => r.error);
      const parts = [
        `Sent to ${sum(results, "recipients")} people`,
        `${sum(results, "pushed")} push notifications`,
      ];
      if (failed.length) {
        parts.push(
          `failed: ${failed.map((r) => `${schoolName(r.school_id)} (${r.error})`).join(", ")}`,
        );
      }
      setFeedback({
        type: failed.length ? "error" : "success",
        message: parts.join(" · "),
      });
    },
    onError: () => {
      // Some schools may already have been sent to, so re-sending blindly
      // could DM them twice.
      setPreview(null);
      setFeedback({
        type: "error",
        message:
          "The broadcast failed partway or timed out. Check a school's Walky inbox before sending again.",
      });
    },
  });

  const recipientCount = preview ? sum(preview, "recipients") : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Send className="h-6 w-6" />
            Broadcast DM
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Sends a direct message from each school's Walky account to everyone
            at that school. It shows up in their Messages like any DM, and
            replies go to that school's Walky account.
          </p>
        </div>

        {feedback && (
          <Alert variant={feedback.type === "error" ? "destructive" : "default"}>
            <AlertDescription>{feedback.message}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">New broadcast</CardTitle>
            <CardDescription>
              People who blocked Walky, and banned or deactivated accounts, are
              skipped.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>School</Label>
              <Select value={schoolId} onValueChange={setSchoolId} disabled={isLoading}>
                <SelectTrigger className="max-w-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_SCHOOLS}>
                    All schools ({schools.length})
                  </SelectItem>
                  {schools.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.school_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="broadcast_message">Message</Label>
              <Textarea
                id="broadcast_message"
                rows={6}
                maxLength={MAX_LENGTH}
                placeholder="What's new?"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {content.length}/{MAX_LENGTH}
              </p>
            </div>

            <Button
              onClick={() => {
                setFeedback(null);
                previewMutation.mutate();
              }}
              disabled={!content.trim() || !targetIds.length || previewMutation.isPending}
            >
              {previewMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Review
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={!!preview}
        onOpenChange={(open) => !open && !sendMutation.isPending && setPreview(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send to {recipientCount} people?</DialogTitle>
            <DialogDescription>
              This can't be undone. Each person gets it as a DM from Walky.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="max-h-40 overflow-y-auto rounded-md border p-3 text-sm space-y-1">
              {preview?.map((r) => (
                <div key={r.school_id} className="flex justify-between gap-4">
                  <span>{schoolName(r.school_id)}</span>
                  <span className={r.error ? "text-destructive" : "text-muted-foreground"}>
                    {r.error ?? r.recipients}
                  </span>
                </div>
              ))}
            </div>
            <div className="rounded-md bg-muted p-3 text-sm whitespace-pre-wrap">
              {content.trim()}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPreview(null)}
              disabled={sendMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => sendMutation.mutate()}
              disabled={!recipientCount || sendMutation.isPending}
            >
              {sendMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default WalkyBroadcastPage;
